import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const { FiX, FiZap, FiDroplet, FiWind, FiWifi, FiCheck, FiDollarSign, FiActivity } = FiIcons;

const UtilityBillModal = ({ isOpen, onClose, onSave, properties, initialData }) => {
  const [formData, setFormData] = useState({
    propertyId: '',
    type: 'Electricity',
    billingMonth: format(new Date(), 'yyyy-MM'),
    amount: '',
    usageValue: '',
    usageUnit: 'kWh',
    notes: '',
    status: 'Paid'
  });

  const utilityTypes = [
    { name: 'Electricity', icon: FiZap, unit: 'kWh' },
    { name: 'Water', icon: FiDroplet, unit: 'Gal' },
    { name: 'Gas', icon: FiWind, unit: 'Therms' },
    { name: 'Internet', icon: FiWifi, unit: 'Mbps' },
    { name: 'Other', icon: FiActivity, unit: 'Units' }
  ];

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        propertyId: '',
        type: 'Electricity',
        billingMonth: format(new Date(), 'yyyy-MM'),
        amount: '',
        usageValue: '',
        usageUnit: 'kWh',
        notes: '',
        status: 'Paid'
      });
    }
  }, [initialData, isOpen]);

  const handleTypeChange = (type) => {
    const selected = utilityTypes.find(t => t.name === type);
    setFormData({ ...formData, type, usageUnit: selected.unit });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-200">
        <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-amber-100 text-amber-600 rounded-xl">
              <SafeIcon icon={FiZap} />
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900">{initialData ? 'Audit Bill' : 'Log Utility Bill'}</h2>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Consumption Tracking</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <SafeIcon icon={FiX} size={24} />
          </button>
        </div>

        <form className="p-8 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Property</label>
              <select required className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500" value={formData.propertyId} onChange={e => setFormData({ ...formData, propertyId: e.target.value })}>
                <option value="">Select Property...</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Utility Type</label>
              <select className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none" value={formData.type} onChange={e => handleTypeChange(e.target.value)}>
                {utilityTypes.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Billing Month</label>
              <input type="month" className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none" value={formData.billingMonth} onChange={e => setFormData({ ...formData, billingMonth: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Amount ($)</label>
              <div className="relative">
                <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input required type="number" step="0.01" className="w-full pl-9 p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Usage ({formData.usageUnit})</label>
              <div className="relative">
                <SafeIcon icon={FiActivity} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input required type="number" className="w-full pl-9 p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none" value={formData.usageValue} onChange={e => setFormData({ ...formData, usageValue: e.target.value })} placeholder="Usage value" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Internal Memo</label>
            <textarea rows="2" className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-sm font-medium outline-none" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="e.g. Higher usage due to AC repairs..." />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 font-bold text-gray-400 hover:text-gray-600 transition">Cancel</button>
            <button type="button" onClick={() => { onSave(formData); onClose(); }} className="flex-1 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 flex items-center justify-center gap-2">
              <SafeIcon icon={FiCheck} /> {initialData ? 'Update Record' : 'Save Bill'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UtilityBillModal;